/**
 * device-session — 서명된 요청이 기다릴 수 있는 "기기 세션이 섰다" 신호.
 *
 * **왜 필요한가.** `useDeviceIdentity` 는 렌더를 막지 않는다(그 훅의 주석이 그렇게 정한다).
 * 그런데 세션이 서기까지는 기기 id 해석 + 동적 import + 네트워크 POST 가 걸려서 수백 ms 다.
 * 그동안 프로필 게이트(`restoreProfile`)와 큐 flush(`syncProfile`)가 이미 발사되어,
 * 서명 없는 요청이 나가고 `403 MissingAuthenticationTokenException` 으로 끊긴다.
 * 실제 부팅 네트워크 순서가 `mine` · `mine` · `register-device` 였다.
 *
 * 훅 순서를 바꿔서는 못 고친다 — 비동기라 어차피 진다. 요청하는 쪽이 세션을 기다려야 한다.
 *
 * **상한이 있는 기다림인 이유.** 기기 등록은 영영 실패할 수 있다(백엔드 다운·오프라인).
 * 그때 무한히 기다리면 앱이 안 열린다. 호출자가 상한을 정하고, 못 서면 `false` 를 받아
 * 각자의 폴백으로 간다 — 게이트는 `/setup` 으로, 큐는 다음 기회로.
 *
 * **첫 시도가 실패하면 상한을 채우지 않는다.** 훅은 실패 뒤 1초부터 백오프로 재시도하므로
 * 게이트의 상한(2초) 안에 다시 설 가능성은 낮다. 그런데도 기다리던 쪽은 실패를 몰라 상한을
 * 다 채웠고, 첫 실행의 부팅 대기 화면이 그만큼 길었다(API 가 안 닿는 dev 시뮬레이터에서 매번
 * ≈2초). 그래서 "첫 시도가 끝났다"도 신호로 둔다 — 그 뒤의 기다림은 곧바로 지금 상태를 답한다.
 * 재시도가 나중에 성공하면 그때부터는 `true` 다.
 */

let ready = false;
/** 첫 시도가 성공이든 실패든 끝나면 풀린다. 기다리던 쪽은 이때 `ready` 를 읽는다. */
let settle: (() => void) | null = null;
const settledPromise = new Promise<void>((resolve) => {
  settle = resolve;
});

/** 기기 세션이 섰다고 알린다. 두 번째부터는 아무것도 하지 않는다. */
export const markDeviceSessionReady = (): void => {
  if (ready) {
    return;
  }
  ready = true;
  settle?.();
};

/**
 * 등록 시도가 실패했다고 알린다 — 기다리던 쪽은 상한을 채우지 않고 `false` 를 받는다.
 * 재시도 중에도 다시 불리지만 풀린 약속을 다시 푸는 것이라 바뀌는 것이 없다.
 */
export const markDeviceSessionFailed = (): void => {
  settle?.();
};

export const isDeviceSessionReady = (): boolean => ready;

/**
 * 세션이 설 때까지 최대 `timeoutMs` 기다린다. 반환값은 **기다린 결과 세션이 섰는지**다.
 * 이미 서 있거나 첫 시도가 실패로 끝났으면 기다리지 않는다.
 */
export const waitForDeviceSession = async (
  timeoutMs: number,
): Promise<boolean> => {
  if (ready) {
    return true;
  }
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<void>((resolve) => {
    timer = setTimeout(resolve, timeoutMs);
  });
  try {
    await Promise.race([settledPromise, timeout]);
  } finally {
    clearTimeout(timer);
  }
  return ready;
};
